import { EnvironmentsTabKeys, RepositoriesTabKeys } from '@/utils/tabs'
import { composePathAndParams, joinValues, TabDescriptor } from '@/utils/urls'

import { ProjectType, ScanType } from '@/types/index.d'

const urls = {
  getBasePath: () => '/',
  legacy: {
    getConnections: (workspaceId: string) =>
      `/workspace/${workspaceId}/connections`,
  },
  analytics: {
    getHardening: `/insights/hardening`,
    getPeople: `/insights/people`,
    getSecurity: `/insights/security`,
    getImpact: `/insights/impact`,
    getContributor: (contributorId: string) =>
      `/insights/people/${contributorId}`,
  },
  environments: {
    getPullRequestRuleProblemsPerLocation: (
      projectId: string,
      pullRequestId: string,
      ruleId: string
    ) =>
      `/environments/${projectId}/pr-issues/${pullRequestId}/rules/${ruleId}/by_location`,
    getRuleProblemsPerLocation: (
      projectId: string,
      scanId: string,
      ruleId: string,
      location?: string
    ) =>
      `/environments/${projectId}/issues/${scanId}/rules/${ruleId}/by_location?location=${location}`,
    getEnvironmentUrl: (environmentId: string): string =>
      `/environments/${environmentId}`,
    getSettingTabUrl: (environmentId: string, tabKey: string): string =>
      `${urls.environments.getEnvironmentUrl(environmentId)}/settings/${tabKey}`,
    getChildTabUrl: (
      environmentId: string,
      childTab: EnvironmentsTabKeys,
      parentTab: EnvironmentsTabKeys,
      id: string
    ): string =>
      `/environments/${environmentId}/${parentTab?.toLowerCase()}/${id}/${childTab
        ?.substring(parentTab.length)
        .toLowerCase()}`,
    getIssues: (environmentId: string, scanId: string): string =>
      `${urls.environments.getEnvironmentUrl(
        environmentId
      )}/${EnvironmentsTabKeys.ISSUES.toLowerCase()}/${scanId}`,
    getIssuesPerPolicy: (
      repositoryId: string,
      scanId: string,
      policyId: string
    ): string =>
      `${urls.environments.getEnvironmentUrl(
        repositoryId
      )}/${EnvironmentsTabKeys.ISSUES.toLowerCase()}/${scanId}/policies/${policyId}`,
    getIssuesPerPolicyRule: (
      repositoryId: string,
      scanId: string,
      policyId: string,
      ruleId: string
    ): string =>
      `${urls.environments.getEnvironmentUrl(
        repositoryId
      )}/${EnvironmentsTabKeys.ISSUES.toLowerCase()}/${scanId}/policies/${policyId}/rules/${ruleId}`,
    getIssuesPerRule: (
      repositoryId: string,
      scanId: string,
      ruleId: string
    ): string => {
      return joinValues(
        `${urls.environments.getEnvironmentUrl(
          repositoryId
        )}/${EnvironmentsTabKeys.ISSUES.toLowerCase()}/${scanId}/rules/${ruleId}`
      )
    },
    getOverviewUrl: (environmentId: string, scanId: string): string =>
      `${urls.environments.getEnvironmentUrl(environmentId)}/overview/${scanId}`,
    getTabUrl: (
      environmentId: string,
      tab: EnvironmentsTabKeys,
      id?: string
    ): string =>
      id
        ? `/environments/${environmentId}/${tab?.toLowerCase()}/${id}`
        : `/environments/${environmentId}/${tab?.toLowerCase()}`,
    newEnvironmentWizard: `/environments/new`,
  },
  projects: {
    getIssuesDetails: (issueId: string): string => `/issue-details/${issueId}`,
    getProjectUrl: (projectId: string, projectType: ProjectType): string =>
      projectType === 'GIT'
        ? urls.repositories.getRepositoryUrl(projectId)
        : urls.environments.getEnvironmentUrl(projectId),
    getTabUrl: (
      projectId: string,
      projectType: ProjectType,
      tabDescriptor: TabDescriptor
    ): string =>
      projectType === 'GIT'
        ? urls.repositories.getTabUrl(
            projectId,
            RepositoriesTabKeys[tabDescriptor?.key]
          )
        : urls.environments.getTabUrl(
            projectId,
            EnvironmentsTabKeys[tabDescriptor?.key]
          ),
  },
  repositories: {
    getPullRequestRuleProblemsPerLocation: (
      projectId: string,
      pullRequestId: string,
      ruleId: string,
      location?: string
    ) =>
      `/repositories/${projectId}/pr-issues/${pullRequestId}/rules/${ruleId}/by_location?location=${location}`,
    getRuleProblemsPerLocation: (
      projectId: string,
      scanId: string,
      ruleId: string,
      location?: string
    ) =>
      `/repositories/${projectId}/issues/${scanId}/rules/${ruleId}/by_location?location=${location}`,
    getBranchUrl: (repositoryId: string, branchName: string): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.BRANCHES.toLowerCase()}/${branchName}`,
    getChildTabUrl: (
      repositoryId: string,
      childTab: RepositoriesTabKeys,
      parentTab: RepositoriesTabKeys,
      id: string
    ): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${parentTab?.toLowerCase()}/${id}/${childTab
        ?.substring(parentTab.length)
        .toLowerCase()}`,
    getDiffChanges: (repositoryId: string, branchName: string): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.DIFFS.toLowerCase()}/${RepositoriesTabKeys.BRANCHES.toLowerCase()}/${branchName}`,
    getPullRequest: (repositoryId: string, branchName: string): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.PULLREQUESTS.toLowerCase()}/${RepositoriesTabKeys.BRANCHES.toLowerCase()}/${branchName}`,
    getRepositoryUrl: (repositoryId: string): string =>
      `/repositories/${repositoryId}`,
    getIssues: (repositoryId: string, scanId: string): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}`,
    getDiffChangesIssues: (
      repositoryId: string,
      pullRequestId: string
    ): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.DIFFS.toLowerCase()}-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${pullRequestId}`,
    getPullRequestsIssues: (
      repositoryId: string,
      pullRequestId: string
    ): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/pr-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${pullRequestId}`,
    getDiffChangesIssuesPerPolicy: (
      repositoryId: string,
      pullRequestId: string,
      policyId: string
    ): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.DIFFS.toLowerCase()}-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${pullRequestId}/policies/${policyId}`,
    getIssuesPerPolicy: (
      repositoryId: string,
      scanId: string,
      policyId: string
    ): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}/policies/${policyId}`,

    getPullRequestsIssuesPerPolicy: (
      repositoryId: string,
      pullRequestId: string,
      policyId: string
    ): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/pr-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${pullRequestId}/policies/${policyId}`,
    getIssuesPerPolicyRule: (
      repositoryId: string,
      policyId: string,
      ruleId: string,
      scanId: string,
      scanType: ScanType
    ): string => {
      switch (scanType) {
        case 'DIFFS':
          return `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/diffs-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}/policies/${policyId}/rules/${ruleId}`
        case 'PULL_REQUEST':
          return `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/pr-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}/policies/${policyId}/rules/${ruleId}`
        default:
          return `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}/policies/${policyId}/rules/${ruleId}`
      }
    },
    getFixbotPerPolicyRule: (
      repositoryId: string,
      scanId: string,
      policyId: string,
      ruleId: string,
      scanType: ScanType,
      params: { legacy_code: boolean }
    ): string => {
      const path = joinValues(
        `${urls.repositories.getIssuesPerPolicyRule(repositoryId, policyId, ruleId, scanId, scanType)}/fixbot`
      )
      return composePathAndParams(path, params)
    },
    getIssuesPerRule: (
      repositoryId: string,
      ruleId: string,
      scanId: string,
      scanType: ScanType,
      scanToolbar?: {
        isLegacyCodeAvailable: boolean
      }
    ): string => {
      const defaultUrl = composePathAndParams(
        joinValues(
          urls.repositories.getRepositoryUrl(repositoryId),
          RepositoriesTabKeys.ISSUES.toLowerCase(),
          `${scanId}/rules/${ruleId}`
        ),
        scanToolbar && { legacy_code: scanToolbar?.isLegacyCodeAvailable }
      )

      switch (scanType) {
        case 'DIFFS':
          return `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/diffs-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}/rules/${ruleId}`
        case 'PULL_REQUEST':
          return `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/pr-${RepositoriesTabKeys.ISSUES.toLowerCase()}/${scanId}/rules/${ruleId}`
        default:
          return defaultUrl
      }
    },
    getFixbotPerRule: (
      repositoryId: string,
      scanId: string,
      ruleId: string,
      scanType: ScanType,
      params: { legacy_code: boolean }
    ): string => {
      const path = joinValues(
        `${urls.repositories.getIssuesPerRule(repositoryId, ruleId, scanId, scanType)}/fixbot`
      )

      return composePathAndParams(path, params)
    },
    getSettingsUrl: (repositoryId: string): string =>
      `${urls.repositories.getRepositoryUrl(repositoryId)}/settings`,
    getSettingTabUrl: (repositoryId: string, tabKey: string): string =>
      `${urls.repositories.getRepositoryUrl(repositoryId)}/settings/${tabKey}`,
    getProtectionUrl: (repositoryId: string): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/settings/protection-setup`,
    getSourceControlUrl: (repositoryId: string): string =>
      `${urls.repositories.getRepositoryUrl(
        repositoryId
      )}/settings/source-control`,
    getOverviewUrl: (repositoryId: string, scanId?: string): string =>
      scanId
        ? `${urls.repositories.getRepositoryUrl(repositoryId)}/overview/${scanId}`
        : `${urls.repositories.getRepositoryUrl(repositoryId)}/overview`,
    getTabUrl: (
      repositoryId: string,
      tab: RepositoriesTabKeys,
      id?: string
    ): string =>
      id
        ? `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/${tab?.toLowerCase()}/${id}`
        : `${urls.repositories.getRepositoryUrl(
            repositoryId
          )}/${tab?.toLowerCase()}`,
    newRepositoryWizard: `/repositories/new`,
  },
  settings: {
    connections: `/settings/connections`,
    catalog: `/settings/connections/catalog`,
    getNewPackage: (packageId: string) => `/settings/packages/new/${packageId}`,
    getNewPackageInstallation: (packageId: string) =>
      joinValues('/store', 'new', packageId),
    getNewPolicy: (policyId: string) => `/settings/policies/new/${policyId}`,
    getNewRule: (ruleId: string) => `/settings/rules/new/${ruleId}`,
    getPackage: (packageId: string) => `/settings/packages/${packageId}`,
    getPackageInstallation: (packageId: string) =>
      joinValues('/store', packageId),
    getPolicyRules: (policyId: string) => `/settings/policies/${policyId}`,
    getRule: (ruleId: string) => `/settings/rules/${ruleId}`,
    packages: `/settings/packages`,
    policies: `/settings/policies`,
    rules: `/settings/rules`,
    store: '/store',
    storeNewPackages: '/store#new',
    storeUpdatedPackages: '/store#update',
    usage: `/settings/usage`,
  },
}

export default urls
